import { Injectable } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { DataSecurityLevel as otelDataSecurityLevel, KnownFeatureNames } from '@office-start/telemetry-types';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { CreateLink } from '../../shared/models/create-link.model';
import { FileType } from '../../shared/models/file-item.model';
import { MruFileItem } from '../../shared/mru/mru-file-item.model';
import { CreateDocumentService } from '../../shared/services/create-document.service';
import { JumpListItem, JumpListService } from '../../shared/services/jump-list.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { LocService } from '../../shared/shared-context/loc.service';
import { PwaConfigService } from './pwa-config.service';

const enum JumpListTaskStatus {
    Idle,
    Loading,
    Updating
}

interface PwaJumpListMapping {
    [id: string]: JumpListItem;
}

interface PwaJumpList {
    items: Array<JumpListItem>;
}

/**
 * Service class for managing jump list for PWA
 */
@Injectable()
export class PwaJumpListService implements JumpListService {
    private static readonly LocalStorageJumpListMappingKey = 'PwaJumpListMapping';
    private static readonly LocalStorageJumpListRemovedItemsKey = 'PwaJumpListRemovedItems';
    private static readonly GroupRecent = 'Recent';
    private static readonly GroupRecommended = 'Recommended';
    private static readonly SupportedFileTypeName = new Set(['word', 'excel', 'powerpoint', 'onenote', 'sway', 'pdf', 'forms', 'visio', 'text']);
    private static readonly SupportedTaskFileType = new Set([FileType.Word, FileType.Excel, FileType.PowerPoint]);
    private static readonly MaxRecommended = 3;
    private static readonly MaxRecent = 8;
    private static readonly MaxSavedFiles = 1000;
    private static readonly MaintenanceSavedFiles = 500;
    private static readonly MaxSavedRemovedFiles = 500;

    private taskStatus: JumpListTaskStatus = JumpListTaskStatus.Idle;
    private isUpdateWaiting: boolean = false;
    private tasks: Array<CreateLink> = [];
    private latestRecommendedDocs: Array<MruFileItem> = [];
    private latestRecentDocs: Array<MruFileItem> = [];
    private readonly jumpListEnabled: boolean = false;
    private readonly jumpListFiles: PwaJumpList = { items: [] };
    private readonly jumpListRemovedFiles: Set<string> = new Set();

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly createDocumentService: CreateDocumentService,
        private readonly instrumentationService: InstrumentationService,
        private readonly localStorageService: LocalStorageService,
        private readonly locService: LocService,
        private readonly pwaConfigService: PwaConfigService
    ) {
        // For WebView2, jumpList feature is enabled only for app version's where Windows.ApplicationModel.Core.CoreApplication.exit & Windows.UI.WindowManagement.AppWindow.tryShowAsync winRT projections are available.
        // TODO: Rename this.capabilitiesService.capabilities.isPwaInUwp as it represents if its running in PWA app and not specific to UWP version of the app. Its true for WV2 version of the app as well.
        this.jumpListEnabled = this.capabilitiesService.capabilities.isPwaInUwp && (!this.capabilitiesService.capabilities.isPwaInWebView2 || (this.capabilitiesService.capabilities.isPwaInWebView2
            && typeof (Windows.ApplicationModel.Core) !== 'undefined' && typeof (Windows.ApplicationModel.Core.CoreApplication) !== 'undefined' && typeof (Windows.ApplicationModel.Core.CoreApplication.exit) !== 'undefined'
            && typeof (Windows.UI.WindowManagement) !== 'undefined' && typeof (Windows.UI.WindowManagement.AppWindow) !== 'undefined')) && this.pwaConfigService.config.pwaJumpListEnabled;
        if (this.jumpListEnabled) {
            this.createDocumentService.getCreateLinks()
                .then(createLinks => {
                    this.tasks = createLinks;
                    this.updateJumpList();
                })
                .catch(error => {
                    const errorParams = {};
                    errorParams[InstrumentationParam.Error] = (error || '').toString();
                    errorParams[InstrumentationParam.Action] = 'GetCreateLinks';
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaJumpList, errorParams, [], DataSecurityLevel.Enhanced);
                    logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.PwaJumpList, params: errorParams }, otelDataSecurityLevel.Enhanced);
                });
        }
    }

    public registerRecommendedJumpList(recommendedDocs: Array<MruFileItem>) {
        if (this.jumpListEnabled) {
            let needUpdate: boolean = false;
            if (recommendedDocs.length !== this.latestRecommendedDocs.length) {
                needUpdate = true;
            } else {
                for (let i = 0; i < recommendedDocs.length; i++) {
                    if (this.latestRecommendedDocs[i].url !== recommendedDocs[i].url) {
                        needUpdate = true;
                        break;
                    }
                }
            }
            if (needUpdate) {
                this.latestRecommendedDocs = JSON.parse(JSON.stringify(recommendedDocs));
                this.updateJumpList();
            }
        }
    }

    public registerRecentJumpList(recentDocs: Array<MruFileItem>) {
        if (this.jumpListEnabled) {
            let needUpdate: boolean = false;
            if (recentDocs.length !== this.latestRecentDocs.length) {
                needUpdate = true;
            } else {
                for (let i = 0; i < recentDocs.length; i++) {
                    if (this.latestRecentDocs[i].url !== recentDocs[i].url) {
                        needUpdate = true;
                        break;
                    }
                }
            }
            if (needUpdate) {
                this.latestRecentDocs = JSON.parse(JSON.stringify(recentDocs));
                this.updateJumpList();
            }
        }
    }

    public getJumpListItemById(id: string): JumpListItem {
        if (this.jumpListEnabled) {
            try {
                const jumpListMapping: PwaJumpListMapping = this.localStorageService.getGlobal(PwaJumpListService.LocalStorageJumpListMappingKey);
                if (jumpListMapping && jumpListMapping[id] !== undefined) {
                    return jumpListMapping[id];
                }
            } catch (error) {
                const errorParams = {};
                errorParams[InstrumentationParam.Error] = (error || '').toString();
                errorParams[InstrumentationParam.Action] = 'GetJumpListItemById';
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaJumpList, errorParams, [], DataSecurityLevel.Enhanced);
                logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.PwaJumpList, params: errorParams }, otelDataSecurityLevel.Enhanced);
            }
        }

        return null;
    }

    private saveToMapping() {
        let jumpListMapping: PwaJumpListMapping;
        try {
            const value = this.localStorageService.getGlobal(PwaJumpListService.LocalStorageJumpListMappingKey);
            jumpListMapping = value ? value : {};
        } catch (error) {
            jumpListMapping = {};
            const errorParams = {};
            errorParams[InstrumentationParam.Error] = (error || '').toString();
            errorParams[InstrumentationParam.Action] = 'GetJumpListMappingFromLocalStorage';
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaJumpList, errorParams, [], DataSecurityLevel.Enhanced);
            logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.PwaJumpList, params: errorParams }, otelDataSecurityLevel.Enhanced);
        }
        this.jumpListFiles.items.forEach(item => { jumpListMapping[item.jumpListItemId] = item; });

        // Once the number of saved files reach limit, we will remove the oldest files.
        if (Object.keys(jumpListMapping).length >= PwaJumpListService.MaxSavedFiles) {
            // Sort the saved files by last shown time
            const shownTimes: Array<number> = Object.keys(jumpListMapping).map(id => jumpListMapping[id].lastShownTime);
            shownTimes.sort((a, b) => b - a);
            const maintenanceShownTime = shownTimes[PwaJumpListService.MaintenanceSavedFiles];
            Object.keys(jumpListMapping).forEach(id => {
                if (jumpListMapping[id].lastShownTime <= maintenanceShownTime) {
                    delete jumpListMapping[id];
                }
            });
        }

        this.localStorageService.setGlobal(PwaJumpListService.LocalStorageJumpListMappingKey, jumpListMapping);
    }

    private updateRemovedFiles() {
        let savedRemovedFiles: Array<string>;
        try {
            const value = this.localStorageService.getGlobal(PwaJumpListService.LocalStorageJumpListRemovedItemsKey);
            savedRemovedFiles = value ? value : [];
        } catch (error) {
            savedRemovedFiles = [];
        }

        savedRemovedFiles.forEach(item => this.jumpListRemovedFiles.add(item));
        if (this.jumpListRemovedFiles.size >= PwaJumpListService.MaxSavedRemovedFiles) {
            this.jumpListRemovedFiles.clear();
        }
        this.localStorageService.setGlobal(PwaJumpListService.LocalStorageJumpListRemovedItemsKey, [...this.jumpListRemovedFiles]);
    }

    private generateJumpListItemId(url: string): string {
        const cryptography = Windows.Security.Cryptography;
        const binaryUrl = cryptography.CryptographicBuffer.convertStringToBinary(url, cryptography.BinaryStringEncoding.utf8);
        const hashResult = cryptography.Core.HashAlgorithmProvider.openAlgorithm('SHA256').hashData(binaryUrl);
        return cryptography.CryptographicBuffer.encodeToHexString(hashResult);
    }

    private addGroupToList(list: Windows.UI.StartScreen.JumpList, docs: Array<MruFileItem>, maxSize: number, group: string, groupName: string) {
        let docsAdded = 0;
        for (let i = 0; i < docs.length; i++) {
            const doc = docs[i];
            const generatedId = this.generateJumpListItemId(doc.url);
            if (!this.jumpListRemovedFiles.has(generatedId)) {
                const listItem: JumpListItem = {
                    application: doc.application,
                    fileName: doc.fileName,
                    fileType: doc.fileType,
                    fileTypeName: doc.fileTypeName,
                    group,
                    jumpListItemId: generatedId,
                    isLocal: doc.isLocal,
                    url: doc.webUrl,
                    canonicalUrl: doc.canonicalUrl,
                    lastShownTime: Date.now()
                };
                if (!doc.isLocal) {
                    listItem.docId = doc.docId;
                    listItem.driveId = doc.driveId;
                    listItem.isOnPremOrVanity = doc.isOnPremOrVanity;
                }
                this.jumpListFiles.items.push(listItem);
                const args = JSON.stringify({ id: listItem.jumpListItemId });
                const item = Windows.UI.StartScreen.JumpListItem.createWithArguments(args, doc.title);
                item.description = doc.title;
                item.groupName = groupName;
                const logoPath = PwaJumpListService.SupportedFileTypeName.has(listItem.fileTypeName.toLowerCase()) ? ('ms-appx:///images/File' + listItem.fileTypeName + '32x32.png') : 'ms-appx:///images/FileText32x32.png';
                item.logo = new Windows.Foundation.Uri(logoPath);
                list.items.append(item);
                if (++docsAdded === maxSize) {
                    return;
                }
            }
        }
    }

    private updateJumpList() {
        if (this.taskStatus === JumpListTaskStatus.Idle) {
            this.taskStatus = JumpListTaskStatus.Loading;
            this.jumpListFiles.items = [];
            Windows.UI.StartScreen.JumpList.loadCurrentAsync().then(
                list => {
                    this.taskStatus = JumpListTaskStatus.Updating;
                    // Update the set of removed jump list files
                    list.items.forEach(item => {
                        if (item.removedByUser) {
                            const args = JSON.parse(item.arguments);
                            if (typeof args.id !== 'undefined') {
                                this.jumpListRemovedFiles.add(args.id);
                            }
                        }
                    });
                    this.updateRemovedFiles();

                    list.systemGroupKind = Windows.UI.StartScreen.JumpListSystemGroupKind.none;
                    list.items.clear();

                    //Add create new file tasks
                    for (let i = 0; i < this.tasks.length; i++) {
                        const task = this.tasks[i];
                        if (PwaJumpListService.SupportedTaskFileType.has(task.workload.FileType)) {
                            const title = this.locService.format(this.locService.PwaJumpListNewFileText, task.title);
                            const args = JSON.stringify({ createFileType: FileType[task.workload.FileType] });
                            const item = Windows.UI.StartScreen.JumpListItem.createWithArguments(args, title);
                            item.description = title;
                            item.groupName = '';
                            const logoPath = 'ms-appx:///images/App' + FileType[task.workload.FileType] + '32x32.png';
                            item.logo = new Windows.Foundation.Uri(logoPath);
                            list.items.append(item);
                        }
                    }
                    this.addGroupToList(list, this.latestRecommendedDocs, PwaJumpListService.MaxRecommended, PwaJumpListService.GroupRecommended, this.locService.RecommendedDocumentsSectionTitle);
                    this.addGroupToList(list, this.latestRecentDocs, PwaJumpListService.MaxRecent, PwaJumpListService.GroupRecent, this.locService.RecentDocumentsSectionTitleLowercase);
                    list.saveAsync().then(
                        () => {
                            const params = {};
                            params[InstrumentationParam.State] = 'Success';
                            params[InstrumentationParam.Action] = 'SaveJumpList';
                            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaJumpList, params, [], DataSecurityLevel.Enhanced);
                            logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.PwaJumpList, params }, otelDataSecurityLevel.Enhanced);
                            // Save the jump list info to local storage.
                            this.saveToMapping();
                            this.completeCurrentTask();
                        },
                        error => {
                            const errorParams = {};
                            errorParams[InstrumentationParam.Error] = (error || '').toString();
                            errorParams[InstrumentationParam.Action] = 'SaveJumpList';
                            logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.PwaJumpList, params: errorParams }, otelDataSecurityLevel.Enhanced);
                            this.completeCurrentTask();
                        }
                    );
                },
                error => {
                    const errorParams: InstrumentationParams = {};
                    errorParams[InstrumentationParam.Error] = (error || '').toString();
                    errorParams[InstrumentationParam.Action] = 'LoadJumpList';
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaJumpList, errorParams, [], DataSecurityLevel.Enhanced);
                    this.taskStatus = JumpListTaskStatus.Idle;
                        if (this.isUpdateWaiting) {
                            this.isUpdateWaiting = false;
                            this.updateJumpList();
                        }
                });
        } else if (this.taskStatus === JumpListTaskStatus.Updating) {
            // Task status is updating, set isUpdateWaiting to true, will trigger another update after the current one is completed.
            this.isUpdateWaiting = true;
        }
    }

    private completeCurrentTask() {
        this.taskStatus = JumpListTaskStatus.Idle;
        if (this.isUpdateWaiting) {
            this.isUpdateWaiting = false;
            this.updateJumpList();
        }
    }
}
